import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../../lib/api";
import type { Collection } from "../library/types";
import type { ChatMessage, Conversation, AskResult } from "./types";

const SOURCE_TYPE_LABELS: Record<string, string> = { pdf: "PDF", article: "Artikel", note: "Notiz", podcast: "Transkript" };

function formatAnchor(c: AskResult["citations"][number]): string | null {
  if (c.pdfPageFrom !== null) return c.pdfPageFrom === c.pdfPageTo ? `Seite ${c.pdfPageFrom}` : `Seiten ${c.pdfPageFrom}–${c.pdfPageTo}`;
  if (c.timeStartSeconds !== null && c.timeEndSeconds !== null) {
    const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
    return `${fmt(c.timeStartSeconds)}–${fmt(c.timeEndSeconds)}`;
  }
  return null;
}

function CitationList({ citations }: { citations: AskResult["citations"] }) {
  if (citations.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.6rem", paddingTop: "0.6rem", borderTop: "1px solid var(--pw-color-surface-flat-border)" }}>
      {citations.map((c) => (
        <Link
          key={c.index}
          to={`/library/${c.sourceId}`}
          style={{ display: "flex", gap: "0.5rem", fontSize: "0.78rem", color: "var(--pw-color-text-muted)", textDecoration: "none" }}
        >
          <span style={{ flexShrink: 0, fontWeight: 700, color: "var(--pw-color-accent-strong)" }}>[{c.index}]</span>
          <span>
            <strong style={{ color: "var(--pw-color-text)" }}>{c.title}</strong>{" "}
            <span style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{SOURCE_TYPE_LABELS[c.sourceType] ?? c.sourceType}</span>
            {formatAnchor(c) && <span> · {formatAnchor(c)}</span>}
          </span>
        </Link>
      ))}
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const citations: AskResult["citations"] = message.citationsJson ? (JSON.parse(message.citationsJson) as AskResult["citations"]) : [];
  const isUser = message.role === "user";
  return (
    <div
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        background: isUser ? "var(--pw-color-accent-bg)" : "var(--pw-color-surface-soft)",
        border: `1px solid ${isUser ? "var(--pw-color-accent-border)" : "var(--pw-color-surface-soft-border)"}`,
        borderRadius: "var(--pw-radius-md)",
        padding: "0.75rem 0.9rem"
      }}
    >
      {!isUser && message.modelId === "local-demo-summarizer" && (
        <span
          style={{ display: "inline-block", marginBottom: "0.4rem", fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 6px", borderRadius: "4px", background: "var(--pw-color-warning-bg, var(--pw-color-accent-bg))", color: "var(--pw-color-warning, var(--pw-color-accent-strong))" }}
        >
          Demo-Antwort (lokal, kein echtes Sprachmodell)
        </span>
      )}
      <div style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>{message.content}</div>
      {!isUser && <CitationList citations={citations} />}
    </div>
  );
}

export function ChatPage() {
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newCollectionId, setNewCollectionId] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const reloadConversations = useCallback(() => {
    void api.get<{ conversations: Conversation[] }>("/api/v1/conversations").then((r) => setConversations(r.conversations));
  }, []);

  useEffect(() => {
    reloadConversations();
    void api.get<{ collections: Collection[] }>("/api/v1/collections").then((r) => setCollections(r.collections));
  }, [reloadConversations]);

  const openConversation = useCallback((id: string) => {
    setActiveId(id);
    setShowNewForm(false);
    void api.get<{ conversation: Conversation; messages: ChatMessage[] }>(`/api/v1/conversations/${id}`).then((r) => setMessages(r.messages));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  async function onStartConversation() {
    setError(null);
    const { id } = await api.post<{ id: string }>("/api/v1/conversations", { collectionId: newCollectionId || undefined });
    setNewCollectionId("");
    reloadConversations();
    openConversation(id);
  }

  async function onDeleteConversation(id: string) {
    await api.delete(`/api/v1/conversations/${id}`);
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
    reloadConversations();
  }

  async function onAsk(e: FormEvent) {
    e.preventDefault();
    if (!activeId || !question.trim() || sending) return;
    setError(null);
    setSending(true);
    const userMessage: ChatMessage = { id: "pending-user", role: "user", content: question, citationsJson: null, modelId: null, createdAt: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    const asked = question;
    setQuestion("");
    try {
      const result = await api.post<AskResult>(`/api/v1/conversations/${activeId}/messages`, { content: asked });
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { id: result.userMessageId, role: "user", content: asked, citationsJson: null, modelId: null, createdAt: new Date().toISOString() },
        { id: result.assistantMessageId, role: "assistant", content: result.answer, citationsJson: JSON.stringify(result.citations), modelId: result.modelId, createdAt: new Date().toISOString() }
      ]);
      reloadConversations();
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setQuestion(asked);
      if (err instanceof ApiRequestError && (err.code === "rate_limited" || err.code === "daily_budget_exceeded")) {
        setError(err.message);
      } else {
        setError(err instanceof ApiRequestError ? err.message : "Frage konnte nicht gesendet werden.");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: "1rem", height: "100%", minHeight: "60vh" }}>
      <section className="pw-card" style={{ width: "260px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "0.6rem", padding: "1rem" }}>
        <button type="button" className="pw-button pw-button--primary" onClick={() => setShowNewForm((v) => !v)}>
          Neue Unterhaltung
        </button>
        {showNewForm && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <select
              value={newCollectionId}
              onChange={(e) => setNewCollectionId(e.target.value)}
              style={{ padding: "0.5rem", borderRadius: "var(--pw-radius-sm)", background: "var(--pw-color-surface)", border: "1px solid var(--pw-color-surface-border)", color: "var(--pw-color-text)" }}
            >
              <option value="">Alle Sammlungen</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button type="button" className="pw-button" onClick={() => void onStartConversation()}>
              Starten
            </button>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", overflowY: "auto" }}>
          {conversations?.map((conv) => (
            <div key={conv.id} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
              <button
                type="button"
                onClick={() => openConversation(conv.id)}
                style={{
                  flex: 1,
                  textAlign: "left",
                  background: activeId === conv.id ? "var(--pw-color-accent-bg)" : "none",
                  border: "none",
                  borderRadius: "var(--pw-radius-sm)",
                  padding: "0.4rem 0.5rem",
                  color: "var(--pw-color-text)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "0.82rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {conv.title ?? new Date(conv.createdAt).toLocaleString("de-DE")}
              </button>
              <button type="button" onClick={() => void onDeleteConversation(conv.id)} title="Löschen" style={{ background: "none", border: "none", color: "var(--pw-color-text-muted)", cursor: "pointer", fontSize: "0.9rem", padding: "0.2rem" }}>
                ✕
              </button>
            </div>
          ))}
          {conversations?.length === 0 && <p style={{ fontSize: "0.78rem", color: "var(--pw-color-text-muted)" }}>Noch keine Unterhaltungen.</p>}
        </div>
      </section>

      <section className="pw-card" style={{ flex: 1, display: "flex", flexDirection: "column", padding: "1rem", minHeight: 0 }}>
        {!activeId ? (
          <p style={{ color: "var(--pw-color-text-muted)", fontSize: "0.9rem" }}>
            Wählt links eine Unterhaltung oder startet eine neue. Der Chat antwortet ausschließlich aus freigegebenen Quellen und ersetzt keine Flugvorbereitung oder offizielle Warnungen.
          </p>
        ) : (
          <>
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.6rem", paddingBottom: "0.6rem" }}>
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
              <div ref={bottomRef} />
            </div>
            {error && <div className="pw-alert pw-alert--error">{error}</div>}
            <form onSubmit={(e) => void onAsk(e)} style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Frage stellen …"
                disabled={sending}
                style={{ flex: 1, padding: "0.65rem 0.8rem", borderRadius: "var(--pw-radius-sm)", border: "1px solid var(--pw-color-surface-border)", background: "var(--pw-color-surface)", color: "var(--pw-color-text)" }}
              />
              <button type="submit" className="pw-button pw-button--primary" disabled={sending || !question.trim()}>
                {sending ? "…" : "Senden"}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
